import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import GetAppDialog from "@/components/GetAppDialog";

export const Logo = () => (
  <div className="flex items-center gap-3">
    <img
      src="/luxeray-app-icon.png"
      alt="LuxeRay Smart Homes app logo"
      className="w-12 h-12 rounded-xl shadow-md shadow-black/40"
      data-testid="brand-app-logo"
    />
    <div>
      <span className="font-disp tracking-wider text-[1.75rem] text-white block leading-none" data-testid="brand-logo">LuxeRay</span>
      <span className="font-mono-lbl text-[10px] text-[#00F0FF] tracking-widest uppercase">Smart Homes Pvt Ltd</span>
    </div>
  </div>
);

const NAV_LINKS = [
  { label: "Townships", href: "/#township", testid: "nav-townships" },
  { label: "Automation", href: "/#automation", testid: "nav-automation" },
  { label: "AIoT Mart", href: "/mart", testid: "nav-aiot-mart" },
  { label: "Enquiry", href: "/#enquiry", testid: "nav-enquiry" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [getApp, setGetApp] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const openGetApp = () => {
    setOpen(false);
    setGetApp(true);
  };

  const go = (href) => {
    setOpen(false);
    if (href.startsWith("/") && !href.includes("#")) {
      navigate(href);
      window.scrollTo(0, 0);
      return;
    }
    const id = href.split("#")[1];
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 120);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <nav className="sticky top-0 z-50 backdrop-blur-xl bg-[#061536]/85 border-b border-[#00F0FF]/20 px-6 py-4" data-testid="main-navbar">
      <div className="max-w-6xl mx-auto flex items-center justify-between">
        <button onClick={() => { navigate("/"); window.scrollTo(0, 0); }} className="text-left" data-testid="nav-home-link">
          <Logo />
        </button>
        <div className="hidden md:flex items-center gap-8 font-mono-lbl text-xs text-white/70">
          {NAV_LINKS.map((l) => (
            <button key={l.testid} onClick={() => go(l.href)} className="hover:text-[#00F0FF] transition-colors uppercase tracking-wider" data-testid={l.testid}>
              {l.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={openGetApp}
            className="hidden sm:block bg-[#FF2E2E] hover:bg-[#E01F1F] text-white px-4 py-2 rounded font-mono-lbl text-xs uppercase tracking-wider font-bold transition-colors shadow-md shadow-black/30"
            data-testid="nav-download-app-btn"
          >
            Download App
          </button>
          <button className="md:hidden text-white" onClick={() => setOpen(!open)} aria-label="Toggle menu" data-testid="nav-mobile-toggle">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="md:hidden mt-4 pb-2 flex flex-col gap-4 font-mono-lbl text-sm text-white/80" data-testid="nav-mobile-menu">
          {NAV_LINKS.map((l) => (
            <button key={l.testid} onClick={() => go(l.href)} className="text-left hover:text-[#00F0FF] uppercase tracking-wider" data-testid={`${l.testid}-mobile`}>
              {l.label}
            </button>
          ))}
          <button onClick={openGetApp} className="text-left text-[#FF2E2E] font-bold uppercase tracking-wider" data-testid="nav-download-app-btn-mobile">
            Download App
          </button>
        </div>
      )}
      <GetAppDialog open={getApp} onOpenChange={setGetApp} />
    </nav>
  );
}
